import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import CreateStory from './components/CreateStory';
import BookReader from './components/BookReader';
import AuthForm from './components/Auth';
import PricingModal from './components/PricingModal';
import Library from './components/Library';
import ChildProfileSetup from './components/ChildProfileSetup';
import StoryCreationProgress, { ProgressState } from './components/StoryCreationProgress';
import Footer from './components/Footer';
import LegalPages from './components/LegalPages';
import BlogPages, { BlogType } from './components/BlogPages';
import LoadingScreen from './components/LoadingScreen';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionManager from './components/SubscriptionManager';
import ParentDashboard from './components/ParentDashboard';
import EmailVerification from './components/EmailVerification';
import { ViewState, StoryConfig, GeneratedBook, User, ChildProfile, ParentControls, Child, VoiceOption } from './types';
import { generateStoryContent, generateBookAssets } from './services/geminiService';
import { authService } from './services/authService';
import { profileService } from './services/profileService';
import { polarService } from './services/polarService';
import { parentControlsService } from './services/parentControlsService';
import { childrenService } from './services/childrenService';
import { loadRuntimeConfig } from './services/runtimeConfig';
import { initSupabase } from './services/supabaseClient';

const blogPathMap: Record<string, ViewState> = {
  '/blog/how-personalized-bedtime-stories-work': 'blog-how-it-works',
  '/blog/best-bedtime-story-apps-2026': 'blog-best-apps',
  '/blog/screen-free-bedtime-routine': 'blog-screen-free',
  '/blog/ai-stories-safety-guide': 'blog-safety-guide',
};

const viewToPathMap: Record<string, string> = Object.fromEntries(
  Object.entries(blogPathMap).map(([path, view]) => [view, path])
);

const App: React.FC = () => {
  const [isBooting, setIsBooting] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);

  const [currentView, setCurrentView] = useState<ViewState>('landing');
  const [generatedBook, setGeneratedBook] = useState<GeneratedBook | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Weaving Story...");
  const [progressState, setProgressState] = useState<ProgressState | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [showPricing, setShowPricing] = useState(false);

  // Auth & Profile State
  const [user, setUser] = useState<User | null>(null);
  const [childProfile, setChildProfile] = useState<ChildProfile | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [parentControls, setParentControls] = useState<ParentControls | null>(null);
  const [appChildren, setAppChildren] = useState<Child[]>([]);
  const [activeChild, setActiveChild] = useState<Child | null>(null);
  const privateViews: ViewState[] = ['create', 'reading', 'library', 'profile-setup', 'subscription-success', 'subscription-manage', 'parent-dashboard'];

  // Initialize App (Config -> Supabase -> Auth)
  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      try {
        // 1. Fetch Runtime Configuration
        const config = await loadRuntimeConfig();

        // 2. Initialize Supabase
        if (!config.supabase.url || !config.supabase.anonKey) {
          const missing = [];
          if (!config.supabase.url) missing.push('VITE_SUPABASE_URL');
          if (!config.supabase.anonKey) missing.push('VITE_SUPABASE_ANON_KEY');
          throw new Error(`Missing Supabase configuration: ${missing.join(', ')}. Please check your environment variables.`);
        }
        initSupabase(config.supabase.url, config.supabase.anonKey);

        // 3. Log Config Status
        polarService.logEnvironment();

        // 4. Check Auth & Query Params
        if (mounted) {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);

          if (currentUser) {
            // Force verification view if not verified
            if (!currentUser.isVerified) {
              setCurrentView('verify-email');
            } else {
              const profile = await profileService.getProfile(currentUser.id);
              if (mounted) setChildProfile(profile);

              // Check for Checkout Success
              const params = new URLSearchParams(window.location.search);
              if (params.get('checkout') === 'success') {
                window.history.replaceState({}, '', '/');
                setCurrentView('subscription-success');
                return;
              }
            }
          }
        }
      } catch (e: any) {
        console.error("Boot failed", e);
        if (mounted) setBootError(e.message || "Failed to load application configuration.");
      } finally {
        if (mounted) setIsBooting(false);
      }
    };

    boot();
    return () => { mounted = false; };
  }, []);

  const handleStartCreating = (prompt?: string) => {
    if (user) {
      if (!user.isVerified) {
        setCurrentView('verify-email');
        return;
      }
      if (childProfile) {
        setCurrentView('create');
      } else {
        setCurrentView('profile-setup');
      }
    } else {
      setCurrentView('login');
    }
    setError(null);
  };

  const handleNavigate = async (view: ViewState, options?: { fromHistory?: boolean }) => {
    // Protect private routes
    if (privateViews.includes(view) && !user) {
      setCurrentView('login');
      return;
    }

    // Protection for unverified users attempting to reach restricted views
    if (user && !user.isVerified && view !== 'verify-email' && view !== 'landing' && !view.includes('support')) {
      setCurrentView('verify-email');
      return;
    }

    setError(null);
    setAuthError(null);

    // Auto-onboarding check
    if (user && user.isVerified && view === 'create' && !childProfile) {
      const profile = await profileService.getProfile(user.id);
      if (profile) {
        setChildProfile(profile);
        setCurrentView(view);
      } else {
        setCurrentView('profile-setup');
      }
      return;
    }

    setCurrentView(view);
    if (!options?.fromHistory) {
      const urlPath = viewToPathMap[view] || '/';
      window.history.pushState({ appView: view }, '', urlPath);
    }
    if (view === 'create') setGeneratedBook(null);
  };

  useEffect(() => {
    const path = window.location.pathname;
    const blogView = blogPathMap[path];
    if (blogView) {
      setCurrentView(blogView);
      window.history.replaceState({ appView: blogView }, '', path);
    } else {
      window.history.replaceState({ appView: 'landing' }, '', window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    const onPopState = async (event: PopStateEvent) => {
      const stateView = event.state?.appView as ViewState | undefined;
      const pathView = blogPathMap[window.location.pathname];
      const nextView = stateView || pathView || 'landing';
      await handleNavigate(nextView, { fromHistory: true });
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [user, childProfile]);

  useEffect(() => {
    if (user) {
      parentControlsService.getControls(user.id).then(setParentControls);
    } else {
      setParentControls(null);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { setAppChildren([]); setActiveChild(null); return; }
    childrenService.migrateFromLegacyProfile(user.id).then(() => {
      childrenService.getChildren(user.id).then(kids => {
        setAppChildren(kids);
        setActiveChild(prev => {
          if (prev && kids.find(k => k.id === prev.id)) return prev;
          return kids[0] || null;
        });
      });
    });
  }, [user]);

  const handleLogout = async () => {
    await authService.logout();
    setUser(null);
    setChildProfile(null);
    setParentControls(null);
    setAppChildren([]);
    setActiveChild(null);
    setCurrentView('landing');
  };

  const handleAuthSubmit = async (data: any) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      let loggedInUser: User;
      if (currentView === 'signup') {
        loggedInUser = await authService.signup(data.name, data.email, data.password);
        setUser(loggedInUser);
        // NEW: Always force verify email screen after signup
        setCurrentView('verify-email');
      } else {
        loggedInUser = await authService.login(data.email, data.password);
        setUser(loggedInUser);

        if (!loggedInUser.isVerified) {
          setCurrentView('verify-email');
        } else {
          const profile = await profileService.getProfile(loggedInUser.id);
          setChildProfile(profile);
          if (profile) {
            setCurrentView('create');
          } else {
            setCurrentView('profile-setup');
          }
        }
      }
    } catch (err: any) {
      setAuthError(err.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailVerified = async () => {
    const refreshedUser = await authService.getCurrentUser();
    setUser(refreshedUser);
    if (refreshedUser?.isVerified) {
      const profile = await profileService.getProfile(refreshedUser.id);
      setChildProfile(profile);
      if (profile) {
        setCurrentView('create');
      } else {
        setCurrentView('profile-setup');
      }
    }
  };

  const handleSaveProfile = async (profile: ChildProfile) => {
    if (!user) return;
    setIsLoading(true);
    setAuthError(null);
    try {
      await profileService.saveProfile(profile);
      setChildProfile(profile);
      setCurrentView('create');
    } catch (err: any) {
      setAuthError(err.message || "Failed to save profile.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateStory = async (config: StoryConfig, voice: VoiceOption) => {
    if (!user || !childProfile) return;

    if (!authService.canGenerateStory(user)) {
      setShowPricing(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgressState({ stage: 'story', completed: 0, total: 0, startTime: Date.now() });
    try {
      setLoadingMessage("Weaving Story...");
      const book = await generateStoryContent(config, childProfile);
      book.voiceName = voice.id;

      setLoadingMessage("Painting World...");
      setProgressState(prev => prev ? { ...prev, stage: 'assets' } : undefined);
      const bookWithAssets = await generateBookAssets(book, childProfile, (completed, total) => {
        setProgressState(prev => prev ? { ...prev, completed, total } : undefined);
      });

      const updatedUser = await authService.incrementUsage(user);
      setUser(updatedUser);
      setGeneratedBook(bookWithAssets);
      setCurrentView('reading');
    } catch (err: any) {
      setError("Failed to generate story. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
      setProgressState(undefined);
    }
  };

  if (isBooting) {
    return <LoadingScreen progress={100} message="Loading magical configuration..." />;
  }

  if (bootError) {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center text-white p-6">
        <div className="bg-red-500/10 border border-red-500/30 p-8 rounded-3xl text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">Connection Error</h2>
          <p className="text-gray-300 mb-6">{bootError}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-3 bg-white text-black rounded-xl font-bold">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isLegalView = currentView === 'privacy' || currentView === 'coppa' || currentView === 'support';
  const isBlogView = currentView.startsWith('blog-');

  return (
    <div className="min-h-screen font-sans selection:bg-gold-500 selection:text-white bg-[#0f0e17]">
      <StoryCreationProgress isLoading={isLoading} progressState={progressState} />

      {currentView !== 'reading' && !isLegalView && !isBlogView && (
        <Header
          currentView={currentView}
          onNavigate={handleNavigate}
          user={user}
          onLogout={handleLogout}
          onOpenPricing={() => setShowPricing(true)}
          childName={activeChild?.name || childProfile?.name}
          appChildren={appChildren}
          activeChild={activeChild}
          onSelectChild={setActiveChild}
        />
      )}

      <main className="relative z-10">
        {currentView === 'landing' && (
          <>
            <Hero onStart={handleStartCreating} />
            <Footer onNavigate={handleNavigate} />
          </>
        )}

        {(currentView === 'login' || currentView === 'signup') && (
          <div className="pt-10">
            <AuthForm
              view={currentView as 'login' | 'signup'}
              onNavigate={handleNavigate}
              onSubmit={handleAuthSubmit}
              isLoading={isLoading}
              error={authError}
            />
          </div>
        )}

        {currentView === 'verify-email' && user && (
          <EmailVerification
            user={user}
            onVerified={handleEmailVerified}
            onBack={() => setCurrentView('login')}
          />
        )}

        {currentView === 'profile-setup' && user && (
          <div className="pt-10">
            <ChildProfileSetup
              userId={user.id}
              onSave={handleSaveProfile}
              existingProfile={childProfile}
              error={authError}
            />
          </div>
        )}

        {currentView === 'library' && user && (
          <Library
            user={user}
            childName={activeChild?.name || childProfile?.name}
            onOpenBook={(book) => {
              setGeneratedBook(book);
              setCurrentView('reading');
            }}
            onCreateNew={() => handleNavigate('create')}
            onGoHome={() => handleNavigate('landing')}
          />
        )}

        {currentView === 'parent-dashboard' && user && (
          <ParentDashboard
            user={user}
            childName={activeChild?.name || childProfile?.name}
            onGoHome={() => handleNavigate('landing')}
          />
        )}

        {currentView === 'create' && (
          <div className="container mx-auto pt-10">
            {error && (
              <div className="max-w-xl mx-auto mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 text-sm rounded-xl text-center">
                {error}
              </div>
            )}
            <CreateStory
              onSubmit={handleCreateStory}
              isLoading={isLoading}
              loadingMessage={loadingMessage}
              childName={activeChild?.name || childProfile?.name}
              parentControls={parentControls}
            />
          </div>
        )}

        {currentView === 'reading' && generatedBook && (
          <BookReader
            book={generatedBook}
            onBack={() => handleNavigate('library')}
          />
        )}

        {currentView === 'subscription-success' && user && (
          <SubscriptionSuccess user={user} onNavigate={handleNavigate} />
        )}

        {currentView === 'subscription-manage' && user && (
          <SubscriptionManager user={user} onNavigate={handleNavigate} />
        )}

        {isLegalView && (
          <LegalPages
            type={currentView as 'privacy' | 'coppa' | 'support'}
            onBack={() => handleNavigate('landing')}
          />
        )}

        {isBlogView && (
          <>
            <BlogPages
              type={currentView as BlogType}
              onBack={() => handleNavigate('landing')}
              onNavigate={handleNavigate}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        )}
      </main>

      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onSuccess={() => {
            setShowPricing(false);
            authService.getCurrentUser().then(u => setUser(u));
          }}
        />
      )}
    </div>
  );
};

export default App;
